<script setup lang="ts">
import axios from 'axios'
import type { MerchantReviewManagerConfig } from '@/types/reviewPlatforms'
import { usePlatformConfig } from '../../composables/usePlatformConfig'
import LinkedRestaurantsList from './LinkedRestaurantsList.vue'
import LinkUberEatsModal from './LinkUberEatsModal.vue'
import EditMappingModal from './EditMappingModal.vue'
import UnlinkConfirmDialog from './UnlinkConfirmDialog.vue'

const props = defineProps<{
  config?: MerchantReviewManagerConfig['ubereats']
  isLoading?: boolean
}>()

const emit = defineEmits<{
  saved: []
}>()

const { t } = useI18n()
const { user } = useLocalStorage()
const notification = useNotification()

const configRef = toRef(props, 'config')

const {
  linkModalOpen,
  editingStoreId,
  unlinkConfirmStoreId,
  isSaving,
  linked,
  availableStoresForLink,
  allStoresForEdit,
  canAddMore,
  editingRow,
  deleteStoreLink,
  confirmUnlink
} = usePlatformConfig('ubereats', configRef, () => emit('saved'))

async function saveNew(payload: { storeId: number; restaurantUuid: string }) {
  isSaving.value = true
  try {
    await axios.post(`/merchants/${user.value.merchant.id}/review-managers/ubereats/setup`, {
      entries: [{ restaurant_uuid: payload.restaurantUuid, store_id: payload.storeId }]
    })
    notification.create('success', { text: t('pages.reviewPlatforms.configuration.ubereats.saveSuccess') })
    linkModalOpen.value = false
    emit('saved')
  } catch {
    notification.create('error', { text: t('pages.reviewPlatforms.configuration.ubereats.saveError') })
  } finally {
    isSaving.value = false
  }
}

async function saveEdit(newStoreId: number) {
  if (!editingRow.value) return
  const currentRow = editingRow.value
  if (newStoreId === currentRow.storeId) {
    editingStoreId.value = null
    return
  }
  isSaving.value = true
  try {
    await deleteStoreLink(currentRow.storeId)
    await axios.post(`/merchants/${user.value.merchant.id}/review-managers/ubereats/setup`, {
      entries: [{ restaurant_uuid: currentRow.externalStoreId, store_id: newStoreId }]
    })
    notification.create('success', { text: t('pages.reviewPlatforms.configuration.ubereats.saveSuccess') })
    editingStoreId.value = null
    emit('saved')
  } catch {
    notification.create('error', { text: t('pages.reviewPlatforms.configuration.ubereats.saveError') })
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div>
    <div v-if="isLoading" class="space-y-2">
      <Skeleton class="h-32 w-full rounded-xl" />
    </div>

    <LinkedRestaurantsList
      v-else
      :title="t('pages.reviewPlatforms.configuration.ubereats.title')"
      :description="t('pages.reviewPlatforms.configuration.ubereats.description')"
      logo-src="/images/reviews/ubereats.svg"
      logo-alt="Uber Eats"
      :linked="linked"
      :can-add-more="canAddMore"
      :add-label="t('pages.reviewPlatforms.configuration.ubereats.addLabel')"
      @add="linkModalOpen = true"
      @edit="(storeId) => (editingStoreId = storeId)"
      @unlink="(storeId) => (unlinkConfirmStoreId = storeId)"
    />

    <LinkUberEatsModal
      :is-open="linkModalOpen"
      :available-stores="availableStoresForLink"
      :is-saving="isSaving"
      @close="linkModalOpen = false"
      @save="saveNew"
    />

    <EditMappingModal
      v-if="editingRow"
      :is-open="editingStoreId !== null"
      :title="t('pages.reviewPlatforms.configuration.ubereats.editTitle')"
      :external-id-label="t('pages.reviewPlatforms.configuration.ubereats.uuidLabel')"
      :external-id="editingRow.externalStoreId"
      :current-store-id="editingRow.storeId"
      :available-stores="allStoresForEdit"
      :is-saving="isSaving"
      @close="editingStoreId = null"
      @save="saveEdit"
    />

    <UnlinkConfirmDialog
      :store-id="unlinkConfirmStoreId"
      :is-saving="isSaving"
      @cancel="unlinkConfirmStoreId = null"
      @confirm="confirmUnlink"
    />
  </div>
</template>
